/**
 * 공유받은 패스워드 페이지
 * 로그인 후 공유받은 패스워드를 관리하는 메인 페이지
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSharedPasswords, getSharedPasswordStats, acceptSharedPassword, rejectSharedPassword, addSharedPasswordByKey, deleteSharedPassword } from '../services/sharedPasswordService';
import { useSharedPassword } from '../services/shareService';
import { testFirestoreWrite } from '../utils/testFirestore';
import { Share, Filter, Search, User, Key, Trash2, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { SHARED_PASSWORD_STATUS, SHARED_PASSWORD_STATUS_LABELS } from '../constants/sharedPassword';
import { SearchBox, ActionButton, FilterLayout } from '../components/UI';
import './SharedPasswordPage.css';

const SharedPasswordPage = () => {
  const { user } = useAuth();
  const [sharedPasswords, setSharedPasswords] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareKeyModal, setShowShareKeyModal] = useState(false);
  const [shareKeyInput, setShareKeyInput] = useState('');
  const [shareKeyLoading, setShareKeyLoading] = useState(false);
  const [shareKeyError, setShareKeyError] = useState(null);
  const [receivedPassword, setReceivedPassword] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // 필터링 상태
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // 공유받은 패스워드 목록 로드
  const loadSharedPasswords = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const [passwords, statistics] = await Promise.all([
        getSharedPasswords(user.uid, {
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder
        }),
        getSharedPasswordStats(user.uid)
      ]);
      
      setSharedPasswords(passwords);
      setStats(statistics);
    } catch (err) {
      console.error('공유받은 패스워드 로드 실패:', err);
      setError('공유받은 패스워드를 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSharedPasswords();
  }, [user, filters]);

  // 개발 모드에서 자동 테스트 (한 번만 실행)
  useEffect(() => {
    if (import.meta.env.DEV && user && !window._firestoreTestDone) {
      window._firestoreTestDone = true;
      console.log('🔍 자동 Firestore 권한 테스트 실행 중...');
      console.log('👤 현재 사용자:', user.uid);
      console.log('📧 이메일:', user.email);
      
      // 간단한 쓰기 테스트
      const testDoc = {
        test: true,
        message: '권한 테스트',
        timestamp: new Date()
      };
      
      import('../services/sharedPasswordService').then(async (module) => {
        const { db } = await import('../services/firebase');
        const { collection, addDoc } = await import('firebase/firestore');
        
        try {
          const docRef = await addDoc(
            collection(db, `users/${user.uid}/sharedPasswords`),
            testDoc
          );
          console.log('✅ Firestore 테스트 성공! 문서 ID:', docRef.id);
          console.log('📄 저장된 경로:', `users/${user.uid}/sharedPasswords/${docRef.id}`);
        } catch (error) {
          console.error('❌ Firestore 테스트 실패!');
          console.error('에러 코드:', error.code);
          console.error('에러 메시지:', error.message);
          console.error('전체 에러:', error);
        }
      });
    }
  }, [user]);

  // 공유키로 패스워드 가져오기
  const handleShareKeySubmit = async () => {
    if (!shareKeyInput.trim()) {
      setShareKeyError('액세스 코드를 입력해주세요.');
      return;
    }

    setShareKeyLoading(true);
    setShareKeyError(null);

    try {
      console.log('액세스 코드로 데이터 조회 시도:', shareKeyInput.trim());
      
      // 액세스 코드로 패스워드 데이터 가져오기 (사용 횟수 차감 포함)
      const sharedData = await useSharedPassword(shareKeyInput.trim(), user.uid);
      
      console.log('조회된 공유 데이터:', sharedData);
      
      if (!sharedData) {
        throw new Error('유효하지 않은 액세스 코드입니다.');
      }

      // 공유받은 패스워드 데이터 생성
      const sharedPasswordData = {
        passwordData: sharedData.passwordData,
        shareInfo: sharedData.shareInfo || {},
        shareMessage: '',
        creatorName: sharedData.shareInfo?.sharedBy || '알 수 없음',
        creatorEmail: '',
        maxUses: sharedData.shareInfo?.maxUses || 1,
        uses: sharedData.shareInfo?.currentUses || 0,
        expiresAt: sharedData.shareInfo?.expiresAt
      };

      try {
        // 먼저 간단한 테스트 실행
        console.log('Firestore 권한 테스트 시작...');
        await testFirestoreWrite(user.uid);
        
        // 액세스 토큰으로 패스워드 추가 시도
        await addSharedPasswordByKey(user.uid, shareKeyInput.trim(), sharedPasswordData);
        
        // 성공 시 모달 닫기
        setShowShareKeyModal(false);
        setShareKeyInput('');
        setShareKeyError(null);
        
        // 목록 새로고침
        await loadSharedPasswords();
        
        alert('공유받은 패스워드가 성공적으로 추가되었습니다!');
      } catch (saveError) {
        console.error('저장 실패, 임시로 표시:', saveError);
        
        // 저장 실패 시 바로 표시 모달로 전환
        setReceivedPassword({
          ...sharedData.passwordData,
          sharedBy: sharedData.shareInfo?.sharedBy || '알 수 없음',
          expiresAt: sharedData.shareInfo?.expiresAt,
          remainingUses: sharedData.shareInfo?.remainingUses
        });
        
        // 공유키 입력 모달 닫기
        setShowShareKeyModal(false);
        setShareKeyInput('');
        setShareKeyError(null);
        
        // 패스워드 표시 모달 열기
        setShowPasswordModal(true);
      }
    } catch (error) {
      console.error('액세스 코드 처리 실패:', error);
      console.error('상세 에러:', error.stack);
      
      // 더 구체적인 에러 메시지 제공
      let errorMessage = '액세스 코드 처리에 실패했습니다.';
      
      if (error.message.includes('유효하지 않은')) {
        errorMessage = '유효하지 않은 액세스 코드입니다. 코드를 다시 확인해주세요.';
      } else if (error.message.includes('만료')) {
        errorMessage = '만료된 공유 링크입니다.';
      } else if (error.message.includes('사용 한도')) {
        errorMessage = '사용 횟수를 초과한 공유 링크입니다.';
      } else if (error.message.includes('비활성화')) {
        errorMessage = '비활성화된 공유 링크입니다.';
      } else {
        errorMessage = error.message || '액세스 코드 처리에 실패했습니다.';
      }
      
      setShareKeyError(errorMessage);
    } finally {
      setShareKeyLoading(false);
    }
  };

  // 필터 변경 핸들러
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 검색 필터링
  const filteredPasswords = sharedPasswords.filter(password => {
    if (!filters.search) return true;
    
    const searchLower = filters.search.toLowerCase();
    return (
      password.senderName?.toLowerCase().includes(searchLower) ||
      password.senderEmail?.toLowerCase().includes(searchLower) ||
      password.shareMessage?.toLowerCase().includes(searchLower) ||
      password.passwordData?.site?.toLowerCase().includes(searchLower)
    );
  });

  // 로딩 상태
  if (loading) {
    return (
      <div className="shared-password-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>공유받은 패스워드를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="shared-password-page">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={loadSharedPasswords} className="btn btn-primary">
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-password-page">
      {/* 액션 버튼들 */}
      <div className="page-header-actions">
        <ActionButton
          variant="primary"
          size="md"
          icon={Key}
          onClick={() => setShowShareKeyModal(true)}
          title="액세스 코드로 패스워드 추가"
        >
          액세스 코드 입력
        </ActionButton>

        {/* 테스트 버튼 (개발 모드에서만) */}
        {import.meta.env.DEV && (
          <ActionButton
            variant="danger"
            size="md"
            onClick={async () => {
              console.log('=== Firestore 권한 테스트 시작 ===');
              console.log('사용자 ID:', user.uid);
              try {
                const result = await testFirestoreWrite(user.uid);
                if (result) {
                  alert('✅ Firestore 권한 테스트 성공! 콘솔을 확인하세요.');
                } else {
                  alert('❌ Firestore 권한 테스트 실패! 콘솔에서 자세한 내용을 확인하세요.');
                }
              } catch (error) {
                console.error('테스트 중 오류:', error);
                alert(`❌ 테스트 실패: ${error.message}`);
              }
              console.log('=== Firestore 권한 테스트 종료 ===');
            }}
            title="Firestore 권한 테스트"
          >
            🧪 권한 테스트
          </ActionButton>
        )}
      </div>

      {/* 필터링 및 검색 */}
      <FilterLayout
        searchSlot={
          <SearchBox
            value={filters.search}
            onChange={(value) => handleFilterChange('search', value)}
            placeholder="공유자, 메시지, 사이트명으로 검색..."
          />
        }
        filterSlot={
          <>
            <span className="filter-label">
              <Filter size={16} />
            </span>

            <select
              className="filter-select"
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="createdAt">생성 시간</option>
              <option value="senderName">공유자</option>
            </select>

            <select
              className="filter-select"
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            >
              <option value="desc">내림차순</option>
              <option value="asc">오름차순</option>
            </select>

            <ActionButton
              variant="ghost"
              size="icon-only"
              icon={RefreshCw}
              onClick={loadSharedPasswords}
              title="새로고침"
            />
          </>
        }
        className="filters-container"
      />

      {/* 공유받은 패스워드 목록 */}
      <div className="shared-passwords-list">
        {filteredPasswords.length === 0 ? (
          <div className="empty-state">
            <Share size={48} className="empty-icon" />
            <h3>공유받은 패스워드가 없습니다</h3>
            <p>다른 사용자가 패스워드를 공유하면 여기에 표시됩니다.</p>
          </div>
        ) : (
          filteredPasswords.map(password => (
            <SharedPasswordCard 
              key={password.id} 
              password={password} 
              onUpdate={loadSharedPasswords}
            />
          ))
        )}
      </div>

      {/* 공유키 입력 모달 */}
      {showShareKeyModal && (
        <div className="modal-overlay" onClick={() => setShowShareKeyModal(false)}>
          <div className="modal-content share-key-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔑 액세스 코드로 패스워드 추가</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowShareKeyModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="share-key-info">
                <p>공유받은 링크에서 액세스 코드를 복사하여 입력하세요.</p>
                <p className="share-key-hint">공유 링크 예시: https://pw-wallet.com/shared/<strong>Xy9Zw8Vu7Ts6Rq5</strong></p>
              </div>
              
              <div className="share-key-input-group">
                <label>액세스 코드 입력</label>
                <input
                  type="text"
                  value={shareKeyInput}
                  onChange={(e) => setShareKeyInput(e.target.value)}
                  placeholder="예: Xy9Zw8Vu7Ts6Rq5"
                  className="share-key-input"
                  disabled={shareKeyLoading}
                />
              </div>
              
              {shareKeyError && (
                <div className="error-message">
                  ⚠️ {shareKeyError}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel" 
                onClick={() => {
                  setShowShareKeyModal(false);
                  setShareKeyInput('');
                  setShareKeyError(null);
                }}
                disabled={shareKeyLoading}
              >
                취소
              </button>
              <button 
                className="btn-primary" 
                onClick={handleShareKeySubmit}
                disabled={shareKeyLoading || !shareKeyInput.trim()}
              >
                {shareKeyLoading ? '처리 중...' : '패스워드 추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 받은 패스워드 표시 모달 */}
      {showPasswordModal && receivedPassword && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content password-display-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔓 공유받은 패스워드</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="password-display-info">
                <div className="info-group">
                  <label>사이트/서비스</label>
                  <div className="info-value">{receivedPassword.title || receivedPassword.site || receivedPassword.name || '제목 없음'}</div>
                </div>
                
                <div className="info-group">
                  <label>사용자명</label>
                  <div className="info-value">{receivedPassword.username || '없음'}</div>
                </div>
                
                <div className="info-group">
                  <label>패스워드</label>
                  <div className="info-value password-value">
                    <span>{receivedPassword.password}</span>
                    <button
                      className="btn-copy-inline"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(receivedPassword.password);
                          alert('패스워드가 복사되었습니다!');
                        } catch (error) {
                          const textArea = document.createElement('textarea');
                          textArea.value = receivedPassword.password;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          alert('패스워드가 복사되었습니다!');
                        }
                      }}
                    >
                      📋 복사
                    </button>
                  </div>
                </div>
                
                {receivedPassword.website && (
                  <div className="info-group">
                    <label>웹사이트</label>
                    <div className="info-value">
                      <a href={receivedPassword.website} target="_blank" rel="noopener noreferrer">
                        {receivedPassword.website}
                      </a>
                    </div>
                  </div>
                )}
                
                <div className="share-meta">
                  <p>공유자: {receivedPassword.sharedBy}</p>
                  {receivedPassword.remainingUses !== undefined && (
                    <p>남은 사용 횟수: {receivedPassword.remainingUses}회</p>
                  )}
                </div>
              </div>
              
              <div className="password-actions">
                <h4>이 패스워드를 어떻게 하시겠습니까?</h4>
                <div className="action-buttons">
                  <button 
                    className="btn-save-password"
                    onClick={() => {
                      alert('개인 패스워드로 저장 기능은 준비 중입니다.');
                    }}
                  >
                    💾 내 패스워드로 저장
                  </button>
                  <button 
                    className="btn-use-once"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    👁️ 이번만 사용
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 공유받은 패스워드 카드 컴포넌트
const SharedPasswordCard = ({ password, onUpdate }) => {
  const { user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [processing, setProcessing] = useState(false);

  const {
    id,
    senderName,
    senderEmail,
    passwordData,
    createdAt,
    shareMessage,
    isUnread
  } = password;


  // 시간 포맷팅
  const formatTime = (timestamp) => {
    if (!timestamp) return '알 수 없음';
    return new Date(timestamp.seconds * 1000).toLocaleString('ko-KR');
  };

  // 삭제 핸들러
  const handleDelete = async (sharedPasswordId) => {
    if (!confirm('이 공유받은 패스워드를 삭제하시겠습니까?')) {
      return;
    }

    setProcessing(true);
    try {
      await deleteSharedPassword(user.uid, sharedPasswordId);
      onUpdate(); // 목록 새로고침
    } catch (error) {
      console.error('삭제 실패:', error);
      alert('삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={`shared-password-card ${isUnread ? 'unread' : ''}`}>
      <div className="card-header">
        <div className="sender-info">
          <User size={16} />
          <div>
            <div className="sender-name">{senderName || '알 수 없음'}</div>
            <div className="sender-email">{senderEmail || '이메일 없음'}</div>
          </div>
        </div>
      </div>

      {shareMessage && (
        <div className="share-message">
          <p>"{shareMessage}"</p>
        </div>
      )}

      <div className="card-content">
        <div className="password-details">
          <div className="detail-row">
            <span className="detail-label">사이트</span>
            <span className="detail-value">{passwordData?.title || passwordData?.site || passwordData?.name || '알 수 없음'}</span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">사용자명</span>
            <span className="detail-value">{passwordData?.username || '알 수 없음'}</span>
          </div>
          
          <div className="detail-row password-row">
            <span className="detail-label">패스워드</span>
            <div className="password-value">
              {showPassword ? (passwordData?.password || '알 수 없음') : '••••••••••••'}
            </div>
            <button 
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? '패스워드 숨기기' : '패스워드 보기'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">공유받은 날짜</span>
            <span className="detail-value">{formatTime(createdAt)}</span>
          </div>
        </div>
      </div>

      <div className="card-actions">
        <button
          onClick={() => handleDelete(id)}
          className="btn btn-danger"
          disabled={processing}
        >
          <Trash2 size={14} />
          삭제
        </button>
      </div>
    </div>
  );
};

export default SharedPasswordPage;